import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { initiateOidcAuth } from '../services/oidcService';
import { initiateCognitoSSO } from '../services/cognitoSSOService';
import { SYBERKEY_KEY_IMG, SYBERKEY_WORDMARK_IMG } from '../branding';
import SyberKeyInlineMark from '../components/SyberKeyInlineMark';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginType, setLoginType] = useState<'standard' | 'sso' | 'cognito'>('sso');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSsoLogin = async () => {
    try {
      setLoading(true);
      setError('');

      // Initiate OIDC authorization flow
      // This will redirect to Auth Portal (IDP) which handles email input, IDP selection and authentication
      await initiateOidcAuth();
      
    } catch (err) {
      console.error('❌ [LoginPage] Error during SSO login:', err);
      setError(err instanceof Error ? err.message : 'Failed to initiate SSO login');
      setLoading(false);
    }
  };

  const handleCognitoSsoLogin = async () => {
    try {
      setLoading(true);
      setError('');

      // Initiate Cognito SSO authorization flow
      // This will redirect to Cognito Hosted UI for authentication
      await initiateCognitoSSO();
      
      // Note: initiateCognitoSSO() will redirect, so we won't reach here
      // If we do, it means there was an error
    } catch (err) {
      console.error('❌ [LoginPage] Error during Cognito SSO login:', err);
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to initiate Cognito SSO login';
      if (err instanceof Error) {
        errorMessage = err.message;
        
        // Check for common issues
        if (err.message.includes('Network error') || err.message.includes('Unable to connect')) {
          errorMessage = `${err.message}\n\nPlease ensure:\n1. The backend API server is running on port 3000\n2. CORS is properly configured\n3. The /cognito-sso/config endpoint is accessible`;
        } else if (err.message.includes('not found') || err.message.includes('404')) {
          errorMessage = `${err.message}\n\nPlease ensure the backend API server is running and the /cognito-sso/config endpoint exists.`;
        }
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleStandardLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // TODO: Implement standard login logic
    // For now, just show an error that standard login is not implemented
    setTimeout(() => {
      setError('Standard login is not yet implemented. Please use SyberKey SSO.');
      setLoading(false);
    }, 500);
  };

  return (
    <div className="login-container">
      {/* Navbar */}
      <nav className="login-navbar">
        <div className="login-navbar-container">
          <Link to="/" className="login-navbar-brand">
            <img src={SYBERKEY_KEY_IMG} alt="" className="login-logo-img" />
            <img src={SYBERKEY_WORDMARK_IMG} alt="SyberKey" className="login-wordmark-img" />
          </Link>
          <Link to="/" className="nav-link">Home</Link>
        </div>
      </nav>

      <div className="login-wrapper">
        <div className="login-card">
          <div className="login-header">
            <div className="syberkey-logo">
              <div className="logo-icon">SK</div>
              <h1>Syberkey Pvt Ltd</h1>
            </div>
            <p className="tagline">Employee Portal</p>
          </div>

          <div className="login-content">
            <h2>Sign In</h2>
            <p className="login-description">
              Choose your preferred login method to access your account.
            </p>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Login Type Tabs */}
            <div className="login-tabs">
              <button
                className={`tab-btn ${loginType === 'standard' ? 'active' : ''}`}
                onClick={() => setLoginType('standard')}
              >
                Standard Login
              </button>
              <button
                className={`tab-btn ${loginType === 'sso' ? 'active' : ''}`}
                onClick={() => setLoginType('sso')}
              >
                <SyberKeyInlineMark /> SSO (OIDC)
              </button>
              <button
                className={`tab-btn ${loginType === 'cognito' ? 'active' : ''}`}
                onClick={() => setLoginType('cognito')}
              >
                Cognito SSO
              </button>
            </div>

            {/* Standard Login Form */}
            {loginType === 'standard' && (
              <form onSubmit={handleStandardLogin} className="login-form">
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    className="form-input"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    className="form-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <button 
                  type="submit"
                  className="login-btn standard-login-btn"
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>

                <div className="login-divider">
                  <span>or</span>
                </div>

                <button 
                  type="button"
                  className="login-btn sso-login-btn"
                  onClick={() => setLoginType('sso')}
                  disabled={loading}
                >
                  <span className="btn-icon">🔑</span>
                  <span>
                    Switch to <SyberKeyInlineMark /> SSO
                  </span>
                </button>
              </form>
            )}

            {/* SSO Login (OIDC) */}
            {loginType === 'sso' && (
              <div className="sso-login-section">
                <button 
                  className="syberkey-login-btn"
                  onClick={handleSsoLogin}
                  disabled={loading}
                >
                  <div className="btn-content">
                    <div className="fingerprint-icon">🔑</div>
                    <div className="btn-text">
                      {loading ? (
                        'Redirecting...'
                      ) : (
                        <>
                          Login with <SyberKeyInlineMark /> SSO (OIDC)
                        </>
                      )}
                    </div>
                  </div>
                </button>

                <div className="login-divider">
                  <span>or</span>
                </div>

                <button 
                  type="button"
                  className="login-btn standard-login-btn"
                  onClick={() => setLoginType('cognito')}
                  disabled={loading}
                >
                  Switch to Cognito SSO
                </button>

                <div className="login-features">
                  <div className="feature">
                    <span className="feature-icon">🔒</span>
                    <span>Secure Biometric Authentication</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">⚡</span>
                    <span>Single Sign-On Access</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">📱</span>
                    <span>Mobile App Required</span>
                  </div>
                </div>
              </div>
            )}

            {/* Cognito SSO Login */}
            {loginType === 'cognito' && (
              <div className="sso-login-section">
                <button 
                  className="syberkey-login-btn"
                  onClick={handleCognitoSsoLogin}
                  disabled={loading}
                >
                  <div className="btn-content">
                    <div className="fingerprint-icon">🔐</div>
                    <div className="btn-text">
                      {loading ? 'Redirecting...' : 'Login with Cognito SSO'}
                    </div>
                  </div>
                </button>

                <div className="login-divider">
                  <span>or</span>
                </div>

                <button 
                  type="button"
                  className="login-btn standard-login-btn"
                  onClick={() => setLoginType('sso')}
                  disabled={loading}
                >
                  Switch to <SyberKeyInlineMark /> SSO (OIDC)
                </button>

                <div className="login-features">
                  <div className="feature">
                    <span className="feature-icon">🔒</span>
                    <span>Cognito Hosted UI Authentication</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">⚡</span>
                    <span>Single Sign-On Access</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">🌐</span>
                    <span>Standard OAuth2 Flow</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="login-footer">
            <p>© 2024 Syberkey Pvt Ltd. All rights reserved.</p>
            <p className="powered-by">
              Powered by <SyberKeyInlineMark />
            </p>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p>Redirecting to authentication portal...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;